import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { NotFoundPage } from 'webReservations/errors/NotFound/NotFoundPage';
import { Layout } from 'webReservations/layout/Layout';
import { Icon } from '@components/icon/Icon';
import { ImageGalleryPreview } from '@components/imageGallery/ImageGalleryPreview';
import { LoadingPage } from '@components/LoadingPage';
import { GuestCountFilterPill } from '@shared/availability/GuestCountFilterPill';
import { GuestCountFilterPopover } from '@shared/availability/GuestCountFilterPopover';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { type RestaurantEvent } from '@shared/types/restaurants';
import { centsToWholeDollar } from '@shared/utils/currency';
import { toLongWeekdayMonthDateAndYear } from '@shared/utils/dateFormatters';
import cx from 'classnames';
import { parseISO } from 'date-fns';
import { useState } from 'react';
import typography from '~styles/typography.scss';
import type { RestaurantDetails } from '../restaurant/apiHelpers';
import { useRestaurantEvent } from './hooks';
import { getTierMaxTicketsPerPurchase } from './ticketPurchase/tierAvailability';
import styles from './RestaurantEventPage.scss';
import { RestaurantEventDetailsPageHeader } from './RestaurantEventPageHeader';
import { RestaurantEventTiersListContainer } from './RestaurantEventTiersList';
import { formatTime } from '@shared/utils/formatTime';

export const TicketedEventPageContainer = () => {
  const { restaurantDetails: restaurant } = useRestaurantContext();
  const { data: restaurantEvent, isLoading, error } = useRestaurantEvent();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!restaurantEvent || error) {
    return <NotFoundPage />;
  }

  return (
    <TicketedEventPagePresentational
      restaurant={restaurant}
      restaurantEvent={restaurantEvent}
    />
  );
};

type TicketedEventPageProps = {
  restaurant: RestaurantDetails;
  restaurantEvent: RestaurantEvent;
};

export const TicketedEventPagePresentational = (
  props: TicketedEventPageProps,
) => {
  const { restaurantEvent, restaurant } = props;

  const {
    close: closeGuestCountFilter,
    isOpen: isGuestCountFilterOpen,
    open: openGuestCountFilter,
  } = useIsOpen();

  const [selectedGuestCount, setSelectedGuestCount] = useState(2);

  const maxTicketsPerPurchase = Math.max(
    1,
    ...restaurantEvent.tiers.map((tier) =>
      getTierMaxTicketsPerPurchase(
        tier,
        restaurantEvent.tiers,
        restaurantEvent.capacity,
      ),
    ),
  );

  const purchaseTicketLabel =
    selectedGuestCount > 1 ? 'Purchase tickets' : 'Purchase ticket';

  const eventLocationText =
    restaurantEvent.location?.trim() ||
    restaurantEvent.directions?.trim() ||
    null;

  return (
    <Layout hideBorder>
      <RestaurantEventDetailsPageHeader
        event={restaurantEvent}
        restaurant={restaurant}
      />
      <div className={cx(styles.pageMargin, styles.pageContent)}>
        <div className={styles.container}>
          <div className={styles.contentArea}>
            <section className={styles.photoSection}>
              <h2 className={typography.h6}>Photo Gallery</h2>
              <ImageGalleryPreview
                eventName={restaurantEvent.name}
                images={restaurantEvent.images}
                enableImageCarousel
              />
            </section>
            <section className={styles.descriptionSection}>
              <h2 className={cx(typography.h7_20, styles.descriptionLabel)}>
                Event Description
              </h2>
              <p className={cx(typography.t1, styles.description)}>
                {restaurantEvent.description}
              </p>
            </section>
          </div>
          <aside className={cx(typography.t1, styles.sidebar)}>
            <div className={styles.eventDetailsCard}>
              <div className={styles.detailItem}>
                <h2 className={typography.h6}>Event Details</h2>
              </div>

              <div className={styles.detailItem}>
                <Icon name="calendar" className={styles.detailIcon} />
                <div>
                  <p className={cx(styles.detailValue)}>
                    {toLongWeekdayMonthDateAndYear(
                      parseISO(restaurantEvent.shift.startDate),
                    )}
                  </p>
                </div>
              </div>

              <div className={styles.detailItem}>
                <Icon name="clock" className={styles.detailIcon} />
                <div>
                  <p className={styles.detailValue}>
                    {formatTime(restaurantEvent)}
                  </p>
                </div>
              </div>

              {eventLocationText && (
                <div className={styles.detailItem}>
                  <Icon name="locationArrow" className={styles.detailIcon} />
                  <div>
                    <p className={styles.detailValue}>{restaurant.name}</p>
                    <p className={styles.detailValue}>{eventLocationText}</p>
                  </div>
                </div>
              )}

              <div className={styles.detailItem} data-testid="pricing-detail">
                <Icon name="creditCard" className={styles.detailIcon} />
                <ul className={styles.tierPricingList}>
                  {restaurantEvent.tiers.map((tier) => (
                    <li key={tier.id} className={cx(styles.detailValue)}>
                      {centsToWholeDollar(tier.price)} per {tier.name} guest
                    </li>
                  ))}
                </ul>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.actions}>
                  <span className={typography.t1}>{purchaseTicketLabel}</span>
                  <div className={styles.guestCountPicker}>
                    <GuestCountFilterPill
                      showGuestsLabel
                      closeFilter={closeGuestCountFilter}
                      isOpenFilter={isGuestCountFilterOpen}
                      openFilter={openGuestCountFilter}
                      selectedGuestCount={selectedGuestCount}
                      className={styles.guestCountPicker}
                      popover={
                        <GuestCountFilterPopover
                          onGuestCountSelection={(guestCount) => {
                            setSelectedGuestCount(guestCount);
                            closeGuestCountFilter();
                          }}
                          maxReservationGuests={maxTicketsPerPurchase}
                        />
                      }
                    />
                  </div>
                  <RestaurantEventTiersListContainer
                    tiers={restaurantEvent.tiers}
                    addOns={restaurantEvent.addOns}
                    guestCount={selectedGuestCount}
                    restaurantEventId={restaurantEvent.id}
                    eventCapacity={restaurantEvent.capacity}
                    bookingUnit={restaurantEvent.bookingUnit}
                  />
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
};
